import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Network, ArrowLeft, Edit, Trash2, Layers, Plus,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';

const L2DomainDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('ipam.l2_domains');
  
  const [domain, setDomain] = useState(null);
  const [vlans, setVlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchDomain = async () => {
    try {
      const response = await api.get(`/ipam/l2-domains/${id}`);
      setDomain(response.data);
      setFormData({
        name: response.data.name || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Error al cargar L2 Domain');
      navigate('/ipam/l2-domains');
    }
  };

  const fetchVlans = async () => {
    try {
      const response = await api.get(`/ipam/vlans?l2_domain_id=${id}&limit=100`);
      setVlans(response.data.data || []);
    } catch (error) {
      console.error('Error loading VLANs:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDomain(), fetchVlans()]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/ipam/l2-domains/${id}`, formData);
      toast.success('L2 Domain actualizado');
      setEditModalOpen(false);
      fetchDomain();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ipam/l2-domains/${id}`);
      toast.success('L2 Domain eliminado');
      navigate('/ipam/l2-domains');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!domain) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="l2-domain-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ipam/l2-domains')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Network className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-bold text-foreground">{domain.name}</h1>
                <p className="text-sm text-muted-foreground">L2 Domain (Broadcast Domain)</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={() => setEditModalOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5 text-primary" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Nombre</span>
              <p className="font-medium">{domain.name}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">VLANs Asociadas</span>
              <p className="font-medium text-primary">{vlans.length}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-sm text-muted-foreground">Descripción</span>
              <p className="font-medium">{domain.description || '-'}</p>
            </div>
          </CardContent>
        </Card>

        {/* VLANs List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              VLANs en este Dominio ({vlans.length})
            </CardTitle>
            <Link to="/ipam/vlans">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Nueva VLAN
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {vlans.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No hay VLANs en este dominio</p>
            ) : (
              <div className="space-y-2">
                {vlans.map((vlan) => (
                  <Link 
                    key={vlan.id} 
                    to={`/ipam/vlans/${vlan.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-primary">{vlan.vlan_number}</span>
                      <span className="font-medium">{vlan.name}</span>
                      {vlan.description && (
                        <span className="text-sm text-muted-foreground">- {vlan.description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-muted px-2 py-1 rounded">
                        {vlan.subnet_count || 0} subnets
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Network className="h-5 w-5 text-primary" />
                Editar L2 Domain
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar L2 Domain <strong>{domain.name}</strong>?
              {vlans.length > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Este dominio tiene {vlans.length} VLANs asociadas.
                </span>
              )}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default L2DomainDetail;
